/**
 * TicketSalesCampaignEditor — the create and edit screen for a campaign.
 *
 * Every block is drawn once and the stylesheet arranges them: desktop puts the
 * form on the left and a 300px column on the right (status, booking-app
 * preview, change history); tablet and phone stack status, form, preview and
 * history, with Create / Save in a pinned title bar.
 */
import type { TicketSalesCampaign } from '@bonsai/shared';

import { DashboardsPage } from '../../dashboards/DashboardsPage';
import type { EditorChromeProps } from '../../shared/editor-page/editor-chrome';
import { EditorHeader } from '../../shared/editor-page/EditorHeader';
import { EditorSaveProblem } from '../../shared/editor-page/EditorSaveProblem';
import { CAMPAIGNS_LIST_PATH } from '../ticket-sales-campaigns-paths';

import { easternToday, type CampaignFormValues } from './campaign-form';
import { CampaignBookingPreview } from './CampaignBookingPreview';
import { CampaignFormCard } from './CampaignFormCard';
import { CampaignHistoryCard } from './CampaignHistoryCard';
import { CampaignStatusCard } from './CampaignStatusCard';
import { useCampaignEditor } from './use-campaign-editor';

import '../../shared/editor-page/editor-page.css';

export interface TicketSalesCampaignEditorProps {
  /** The saved campaign being edited; absent for a new one. */
  campaign?: TicketSalesCampaign;
  initial: CampaignFormValues;
  initiallySaved?: boolean;
  persist: (values: CampaignFormValues, base: CampaignFormValues) => Promise<TicketSalesCampaign>;
  onSaved?: (campaign: TicketSalesCampaign) => void;
}

export function TicketSalesCampaignEditor(p: TicketSalesCampaignEditorProps) {
  const editor = useCampaignEditor(p);
  const now = new Date();
  const isNew = !p.campaign;
  const savedName = p.campaign?.name ?? '';

  const chrome: EditorChromeProps = {
    title: isNew ? 'New campaign' : savedName,
    section: 'Marketing',
    listLabel: 'Campaigns',
    crumb: isNew ? 'New' : savedName,
    noun: 'campaign',
    listPath: CAMPAIGNS_LIST_PATH,
    isNew,
    dirty: editor.dirty,
    saved: editor.saved,
    saving: editor.saving,
    onSave: () => void editor.submit(),
  };

  return (
    <DashboardsPage header={<EditorHeader {...chrome} />}>
      {editor.errors.form && <EditorSaveProblem message={editor.errors.form} />}
      <div className="nb-ed-grid">
        <div className="nb-ed-form">
          <CampaignFormCard editor={editor} today={easternToday(now)} />
        </div>
        <div className="nb-ed-status">
          <CampaignStatusCard
            values={editor.values}
            now={now}
            onEnabledChange={(enabled) => editor.set({ enabled })}
          />
        </div>
        <div className="nb-ed-preview">
          <CampaignBookingPreview values={editor.values} />
        </div>
        {p.campaign && (
          <div className="nb-ed-extra">
            <CampaignHistoryCard
              campaignId={p.campaign.id}
              discountType={p.campaign.discountType}
            />
          </div>
        )}
      </div>
    </DashboardsPage>
  );
}
